/**
 * AsyncButton Component
 *
 * A specialized button component that handles async operations with built-in
 * loading states and duplicate submission prevention.
 *
 * Key features:
 * - Instant deduplication via ref-based locking (prevents race conditions)
 * - Automatic loading state with spinner and custom text
 * - Integrates with existing hooks (useOrchestrator, useOptimisticAction)
 * - Supports all Button variants, sizes, and accessibility features
 *
 * @example
 * ```tsx
 * // With external loading state (from useOrchestrator)
 * const { execute, isLoading } = useOrchestrator(Action.UPDATE_TASK);
 * <AsyncButton
 *   onClick={() => execute(data)}
 *   isLoading={isLoading}
 *   icon={IconName.SAVE}
 *   loadingText="Saving..."
 * >
 *   Save Changes
 * </AsyncButton>
 * ```
 *
 * @example
 * ```tsx
 * // With standalone async function (internal loading state)
 * <AsyncButton
 *   onClick={handleCustomAsync}
 *   icon={IconName.TRASH}
 *   loadingText="Deleting..."
 *   variant="destructive"
 * >
 *   Delete
 * </AsyncButton>
 * ```
 */

import * as React from 'react';
import { useState, useRef, useCallback } from 'react';
import { Button, ButtonProps } from './button';
import { Icon, IconName } from '@/icons';
import { cn } from '@/lib/utils';

export interface AsyncButtonProps extends Omit<
  ButtonProps,
  'onClick' | 'disabled'
> {
  /**
   * Async function to execute when button is clicked.
   * The button will automatically show loading state during execution.
   */
  onClick: () => Promise<void>;

  /**
   * Additional disabled logic (e.g., form validation).
   * The button is automatically disabled during loading.
   */
  disabled?: boolean;

  /**
   * External loading state (from useOrchestrator, useOptimisticAction, etc.).
   * If provided, AsyncButton will use this instead of managing internal state.
   */
  isLoading?: boolean;

  /**
   * Text to display while loading.
   * @default "{children}..." or "Loading..." if children is not a string
   */
  loadingText?: string;

  /**
   * Icon to display next to the button text.
   * Will be replaced with a spinner during loading.
   */
  icon?: IconName;

  /**
   * Position of the icon relative to the text.
   * @default 'left'
   */
  iconPosition?: 'left' | 'right';

  /**
   * Callback invoked when the async operation completes (success or error).
   * Note: Error handling (toasts) is managed by orchestrator/hooks.
   * This callback is for UI-specific side effects only.
   */
  onComplete?: (error?: Error) => void;

  /**
   * Button content (text or elements).
   */
  children: React.ReactNode;
}

export const AsyncButton = React.forwardRef<
  HTMLButtonElement,
  AsyncButtonProps
>(
  (
    {
      onClick,
      disabled = false,
      isLoading: externalLoading,
      loadingText,
      icon,
      iconPosition = 'left',
      onComplete,
      children,
      className,
      size = 'default',
      ...buttonProps
    },
    ref
  ) => {
    const [internalLoading, setInternalLoading] = useState(false);
    const isExecutingRef = useRef(false);

    // Use external loading if provided, otherwise use internal state
    const isLoading = externalLoading ?? internalLoading;

    const handleClick = useCallback(async () => {
      // CRITICAL: Ref check is instant, prevents race condition
      // State-based checks (isLoading) are async and allow duplicate clicks
      if (isExecutingRef.current || isLoading || disabled) {
        return;
      }

      isExecutingRef.current = true;
      if (externalLoading === undefined) {
        setInternalLoading(true);
      }

      try {
        await onClick();
        onComplete?.();
      } catch (error) {
        onComplete?.(error as Error);
      } finally {
        isExecutingRef.current = false;
        if (externalLoading === undefined) {
          setInternalLoading(false);
        }
      }
    }, [onClick, isLoading, disabled, externalLoading, onComplete]);

    // Map button size to spinner size
    const spinnerSize = size === 'lg' ? 'md' : 'sm';

    // Generate loading text
    const displayLoadingText =
      loadingText ||
      (typeof children === 'string' ? `${children}...` : 'Loading...');

    return (
      <Button
        {...buttonProps}
        ref={ref}
        onClick={handleClick}
        disabled={disabled || isLoading}
        className={cn('gap-2', className)}
        size={size}
        aria-busy={isLoading}
      >
        {/* Left icon/spinner */}
        {iconPosition === 'left' && (
          <>
            {isLoading ? (
              <Icon
                name={IconName.LOADER}
                size={spinnerSize}
                className="animate-spin"
              />
            ) : icon ? (
              <Icon name={icon} size={spinnerSize} />
            ) : null}
          </>
        )}

        {/* Text */}
        {isLoading ? displayLoadingText : children}

        {/* Right icon/spinner */}
        {iconPosition === 'right' && (
          <>
            {isLoading ? (
              <Icon
                name={IconName.LOADER}
                size={spinnerSize}
                className="animate-spin"
              />
            ) : icon ? (
              <Icon name={icon} size={spinnerSize} />
            ) : null}
          </>
        )}
      </Button>
    );
  }
);

AsyncButton.displayName = 'AsyncButton';
